<template>
    <article class="markdown-body">
        <h2 class="atx" id="introduzione-e-scopo-del-documento">Introduzione e scopo del documento</h2>
        <p>Questo documento ha lo scopo di guidare nell’uso delle <strong>Data API</strong> esposte via <strong>REST</strong>.</p>
        <p>In particolare, la guida spiega come:</p>
        <ol>
            <li>Autenticarsi alle API e ottenere un <strong>Access Token (JWT)</strong></li>
            <li>
                Usare il token per chiamare gli endpoint REST su:
                <code>https://dataapi.digibite.it/rest/wa-mcbookinghub/&lt;entity&gt;</code>
            </li>
        </ol>
        <hr />
        <h2 class="atx" id="url-e-parametri-principali">URL e parametri principali</h2>
        <h3 class="atx" id="autenticazione">Autenticazione</h3>
        <ul>
            <li><strong>Server di autenticazione:</strong> <code>https://keycloak.digibite.it</code></li>
            <li>
                <strong>Endpoint token:</strong>
                <code>https://keycloak.digibite.it/realms/live/protocol/openid-connect/token</code>
            </li>
        </ul>
        <h3 class="atx" id="api-rest">API REST</h3>
        <ul>
            <li><strong>Base URL REST:</strong> <code>https://dataapi.digibite.it/rest/wa-mcbookinghub</code></li>
            <li>
                <strong>Endpoint per entità:</strong>
                <code>https://dataapi.digibite.it/rest/wa-mcbookinghub/&lt;entity&gt;</code>
            </li>
        </ul>
        <h3 class="atx" id="parametri-necessari">Parametri necessari</h3>
        <ul>
            <li><code>client_id</code>: <code>dab-api</code></li>
            <li><code>username</code>: fornito separatamente</li>
            <li><code>password</code>: fornito separatamente</li>
        </ul>
        <hr />
        <h2 class="atx" id="autenticazione-e-ottenimento-dellaccess-token">Autenticazione e ottenimento dell’Access Token</h2>
        <p>L’autenticazione avverrà scambiando username/password con un access token JWT.</p>
        <h3 class="atx" id="richiesta-token-curl">Richiesta token (cURL)</h3>
        <p>Eseguire questo comando sostituendo i placeholder:</p>
        <pre><code class="fenced-code-block language-bash"><span class="token function">curl</span> -sS -X POST <span class="token punctuation">\</span>
  <span class="token string">"https://keycloak.digibite.it/realms/live/protocol/openid-connect/token"</span> <span class="token punctuation">\</span>
  -H <span class="token string">"Content-Type: application/x-www-form-urlencoded"</span> <span class="token punctuation">\</span>
  --data-urlencode <span class="token string">"grant_type=password"</span> <span class="token punctuation">\</span>
  --data-urlencode <span class="token string">"client_id=dab-api"</span> <span class="token punctuation">\</span>
  --data-urlencode <span class="token string">"username=&lt;USERNAME&gt;"</span> <span class="token punctuation">\</span>
  --data-urlencode <span class="token string">"password=&lt;PASSWORD&gt;"</span></code></pre>
        <h3 class="atx" id="risposta">Risposta</h3>
        <p>Si otterrà un JSON simile a questo:</p>
        <pre><code class="fenced-code-block language-json"><span class="token punctuation">{</span>
  <span class="token property">"access_token"</span><span class="token operator">:</span> <span class="token string">"eyJhbGciOiJ..."</span><span class="token punctuation">,</span>
  <span class="token property">"expires_in"</span><span class="token operator">:</span> <span class="token number">300</span><span class="token punctuation">,</span>
  <span class="token property">"refresh_token"</span><span class="token operator">:</span> <span class="token string">"eyJhbGciOiJ..."</span><span class="token punctuation">,</span>
  <span class="token property">"refresh_expires_in"</span><span class="token operator">:</span> <span class="token number">1800</span><span class="token punctuation">,</span>
  <span class="token property">"token_type"</span><span class="token operator">:</span> <span class="token string">"Bearer"</span>
<span class="token punctuation">}</span></code></pre>
        <ul>
            <li><code>access_token</code>: token da usare nelle chiamate REST</li>
            <li><code>expires_in</code>: durata (secondi) del token</li>
            <li><code>refresh_token</code>: token per rigenerare un nuovo access token quando scade</li>
        </ul>
        <hr />
        <h2 class="atx" id="chiamare-le-api-rest-con-il-token">Chiamare le API REST con il token</h2>
        <p>Per ogni chiamata alle API, aggiungere gli header:</p>
        <ul>
            <li><code>Authorization: Bearer &lt;access_token&gt;</code></li>
            <li><code>X-MS-API-ROLE: da_mc_bookinghub</code></li>
        </ul>
        <h3 class="atx" id="esempio-lista-di-record-di-unentita">Esempio: lista di record di un’entità</h3>
        <pre><code class="fenced-code-block language-bash"><span class="token function">curl</span> -sS <span class="token punctuation">\</span>
  -H <span class="token string">"Authorization: Bearer &lt;ACCESS_TOKEN&gt;"</span> <span class="token punctuation">\</span>
  -H <span class="token string">"X-MS-API-ROLE: da_mco_smartcalendar"</span> <span class="token punctuation">\</span>
  -H <span class="token string">"Accept: application/json"</span> <span class="token punctuation">\</span>
  <span class="token string">"https://dataapi.digibite.it/rest/wa-mcbookinghub/&lt;entity&gt;"</span></code></pre>
        <h3 class="atx" id="esempio-dettaglio-di-un-record-per-id">Esempio: dettaglio di un record (per id)</h3>
        <pre><code class="fenced-code-block language-bash"><span class="token function">curl</span> -sS <span class="token punctuation">\</span>
  -H <span class="token string">"Authorization: Bearer &lt;ACCESS_TOKEN&gt;"</span> <span class="token punctuation">\</span>
  -H <span class="token string">"X-MS-API-ROLE: da_mco_smartcalendar"</span> <span class="token punctuation">\</span>
  -H <span class="token string">"Accept: application/json"</span> <span class="token punctuation">\</span>
  <span class="token string">"https://dataapi.digibite.it/rest/wa-mcbookinghub/&lt;entity&gt;/&lt;id&gt;"</span></code></pre>
        <blockquote>
            <p>Nota: se l’entità supporta la chiave <code>id</code>, si esegue la chiamata con <code>/&lt;id&gt;</code>.</p>
        </blockquote>
        <hr />
        <h2 class="atx" id="refresh-del-token">Refresh del token</h2>
        <p>Quando l’access token scade, usare il <code>refresh_token</code> per ottenere un nuovo <code>access_token</code>:</p>
        <pre><code class="fenced-code-block language-bash"><span class="token function">curl</span> -sS -X POST <span class="token punctuation">\</span>
  <span class="token string">"https://keycloak.digibite.it/realms/live/protocol/openid-connect/token"</span> <span class="token punctuation">\</span>
  -H <span class="token string">"Content-Type: application/x-www-form-urlencoded"</span> <span class="token punctuation">\</span>
  --data-urlencode <span class="token string">"grant_type=refresh_token"</span> <span class="token punctuation">\</span>
  --data-urlencode <span class="token string">"client_id=dab-api"</span> <span class="token punctuation">\</span>
  --data-urlencode <span class="token string">"refresh_token=&lt;REFRESH_TOKEN&gt;"</span></code></pre>
        <hr />
        <h2 class="atx" id="entita-disponibili">Entità disponibili</h2>
        <h2 class="atx" id="convenzioni">Convenzioni</h2>
        <ul>
            <li>Chiavi primarie UUID (CHAR(36)), tranne <code>customers</code>.</li>
            <li>Date in millisecondi Unix (BIGINT).</li>
            <li>Audit <code>created_by</code>/<code>updated_by</code> -&gt; <code>users</code>, con <code>ON DELETE SET NULL</code>.</li>
            <li><code>enabled</code> presente in molte entita per disabilitare senza cancellare.</li>
        </ul>
        <h2 class="atx" id="tabelle">Tabelle</h2>
        <h3 class="atx" id="users">users</h3>
        <ul>
            <li>Utenti applicativi con ruolo obbligatorio.</li>
            <li><strong>Enum:</strong> <code>type</code> = <code>User</code>, <code>Admin</code>, <code>SuperAdmin</code>.</li>
        </ul>
        <h3 class="atx" id="roles">roles</h3>
        <ul>
            <li>Ruoli autorizzativi, con flag di default.</li>
            <li>Collegati ai permessi tramite <code>roles_permissions</code>.</li>
        </ul>
        <h3 class="atx" id="countries">countries</h3>
        <ul>
            <li>Anagrafica paesi (ISO). Usata dai visitatori.</li>
        </ul>
        <h3 class="atx" id="commercial_segments">commercial_segments</h3>
        <ul>
            <li>Segmenti commerciali per visitatori.</li>
        </ul>
        <h3 class="atx" id="groups">groups</h3>
        <ul>
            <li>Gruppi utente.</li>
            <li>Usati per assegnazioni e notifiche su risorse.</li>
        </ul>
        <h3 class="atx" id="users_groups">users_groups</h3>
        <ul>
            <li>Relazione molti-a-molti utenti/gruppi.</li>
        </ul>
        <h3 class="atx" id="customers">customers</h3>
        <ul>
            <li>Clienti importati da Salesforce.</li>
        </ul>
        <h3 class="atx" id="resource_types">resource_types</h3>
        <ul>
            <li>Tipologie di risorse prenotabili.</li>
        </ul>
        <h3 class="atx" id="groups_resource_types">groups_resource_types</h3>
        <ul>
            <li>Associa gruppi e tipologie risorsa.</li>
            <li><strong>Flag:</strong> notifiche su creazione/aggiornamento/cancellazione; abilitazione della relazione.</li>
        </ul>
        <h3 class="atx" id="custom_fields">custom_fields</h3>
        <ul>
            <li>Campi personalizzati riusabili, associabili a risorse.</li>
        </ul>
        <h3 class="atx" id="resources">resources</h3>
        <ul>
            <li>Risorse prenotabili, legate a <code>resource_types</code>.</li>
            <li><strong>Vincolo:</strong> capacity &gt;= 0 (0 significa no gestione capacity).</li>
        </ul>
        <h3 class="atx" id="resources_custom_fields">resources_custom_fields</h3>
        <ul>
            <li>Associazione risorse &lt;-&gt; campi personalizzati.</li>
        </ul>
        <h3 class="atx" id="resources_slots">resources_slots</h3>
        <ul>
            <li>Fasce orarie ricorrenti per risorsa.</li>
            <li><strong>Enum:</strong> <code>day_of_week</code> = Monday..Sunday, <code>type</code> = <code>Availability</code> o <code>Maintenance</code>.</li>
            <li><strong>Vincolo:</strong> se entrambe presenti, <code>valid_until_ms</code> &gt; <code>valid_from_ms</code>.</li>
            <li>Unicità per risorsa+giorno+orari+tipo.</li>
        </ul>
        <h3 class="atx" id="reservations">reservations</h3>
        <ul>
            <li>Testata prenotazione.</li>
            <li><strong>Enum:</strong> <code>status</code> = <code>Planned</code>, <code>Canceled</code>, <code>Completed</code>; <code>type</code> = <code>Internal</code>, <code>External</code>.</li>
        </ul>
        <h3 class="atx" id="reservations_resources">reservations_resources</h3>
        <ul>
            <li>Associazione prenotazione &lt;-&gt; risorsa con intervallo temporale.</li>
            <li><strong>Vincolo:</strong> <code>end_ms</code> &gt; <code>start_ms</code>.</li>
        </ul>
        <h3 class="atx" id="reservations_resources_cf">reservations_resources_cf</h3>
        <ul>
            <li>Valori dei campi personalizzati per la coppia prenotazione-risorsa.</li>
            <li><strong>Tipo valore:</strong> booleano esteso (<code>TINYINT</code>).</li>
            <li>Eventuali note.</li>
        </ul>
        <h3 class="atx" id="visitors">visitors</h3>
        <ul>
            <li>Visitatori collegati alle prenotazioni.</li>
            <li><strong>Enum:</strong> <code>_type</code> = <code>ErpProfessional</code>, <code>NewProfessional</code>, <code>NonProfessional</code>.</li>
        </ul>
        <h3 class="atx" id="reservations_visitors">reservations_visitors</h3>
        <ul>
            <li>Relazione molti-a-molti prenotazioni/visitatori.</li>
        </ul>
        <h3 class="atx" id="appreception_invites">appreception_invites</h3>
        <ul>
            <li>Estensione 1:1 della prenotazione con token e stato conferma.</li>
        </ul>
        <h2 class="atx" id="viste">Viste</h2>
        <ul>
            <li><code>view_roles</code>: ruoli con permessi aggregati (array JSON; array vuoto se nessun permesso).</li>
            <li><code>view_users</code>: utenti con permessi del ruolo e gruppi aggregati.</li>
            <li><code>view_groups</code>: gruppi con utenti aggregati.</li>
            <li><code>view_resources</code>: risorse con campi personalizzati aggregati.</li>
            <li><code>view_reservations</code>: prenotazioni con visitatori aggregati.</li>
        </ul>
        <hr />
        <h2 class="atx" id="errori-comuni-e-come-risolverli">Errori comuni (e come risolverli)</h2>
        <h3 class="atx" id="401-unauthorized">401 Unauthorized</h3>
        <p><strong>Cause tipiche:</strong></p>
        <ul>
            <li>mancanza dell’header <code>Authorization</code></li>
            <li>token scaduto</li>
            <li>credenziali errate</li>
        </ul>
        <p><strong>Cosa fare:</strong></p>
        <ul>
            <li>ripetere il login per ottenere un nuovo token</li>
            <li>verificare che l’header sia esattamente: <code>Authorization: Bearer &lt;token&gt;</code></li>
        </ul>
        <h3 class="atx" id="403-forbidden">403 Forbidden</h3>
        <p><strong>Significa:</strong></p>
        <ul>
            <li>token valido, ma non autorizzato per quell’entità/azione</li>
        </ul>
        <p><strong>Cosa fare:</strong></p>
        <ul>
            <li>verificare che l’utente disponga dei permessi necessari per l’entità richiesta</li>
        </ul>
        <h3 class="atx" id="5xx-server-error">5xx Server Error</h3>
        <p><strong>Indica:</strong></p>
        <ul>
            <li>un problema lato servizio/back-end</li>
        </ul>
        <p><strong>Cosa fare:</strong></p>
        <ul>
            <li>riprovare dopo qualche minuto</li>
            <li>se il problema persiste, scrivere a <code>developer@digibite.it</code> indicando l’orario della richiesta e l’endpoint chiamato</li>
        </ul>
    </article>
</template>

<script setup lang="ts"></script>

<style>
@font-face {
    font-family: octicons-link;
    src: url(data:font/woff;charset=utf-8;base64,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)
        format('woff');
}

.markdown-body .octicon {
    display: inline-block;
    fill: currentColor;
    vertical-align: text-bottom;
}

.markdown-body .anchor {
    float: left;
    line-height: 1;
    margin-left: -20px;
    padding-right: 4px;
}

.markdown-body .anchor:focus {
    outline: none;
}

.markdown-body h1 .octicon-link,
.markdown-body h2 .octicon-link,
.markdown-body h3 .octicon-link,
.markdown-body h4 .octicon-link,
.markdown-body h5 .octicon-link,
.markdown-body h6 .octicon-link {
    color: #1b1f23;
    vertical-align: middle;
    visibility: hidden;
}

.markdown-body h1:hover .anchor,
.markdown-body h2:hover .anchor,
.markdown-body h3:hover .anchor,
.markdown-body h4:hover .anchor,
.markdown-body h5:hover .anchor,
.markdown-body h6:hover .anchor {
    text-decoration: none;
}

.markdown-body h1:hover .anchor .octicon-link,
.markdown-body h2:hover .anchor .octicon-link,
.markdown-body h3:hover .anchor .octicon-link,
.markdown-body h4:hover .anchor .octicon-link,
.markdown-body h5:hover .anchor .octicon-link,
.markdown-body h6:hover .anchor .octicon-link {
    visibility: visible;
}

.markdown-body {
    -ms-text-size-adjust: 100%;
    -webkit-text-size-adjust: 100%;
    color: #24292e;
    line-height: 1.5;
    font-family:
        -apple-system,
        BlinkMacSystemFont,
        Segoe UI,
        Helvetica,
        Arial,
        sans-serif,
        Apple Color Emoji,
        Segoe UI Emoji,
        Segoe UI Symbol;
    font-size: 16px;
    line-height: 1.5;
    word-wrap: break-word;
}

.markdown-body .pl-c {
    color: #6a737d;
}

.markdown-body .pl-c1,
.markdown-body .pl-s .pl-v {
    color: #005cc5;
}

.markdown-body .pl-e,
.markdown-body .pl-en {
    color: #6f42c1;
}

.markdown-body .pl-s .pl-s1,
.markdown-body .pl-smi {
    color: #24292e;
}

.markdown-body .pl-ent {
    color: #22863a;
}

.markdown-body .pl-k {
    color: #d73a49;
}

.markdown-body .pl-pds,
.markdown-body .pl-s,
.markdown-body .pl-s .pl-pse .pl-s1,
.markdown-body .pl-sr,
.markdown-body .pl-sr .pl-cce,
.markdown-body .pl-sr .pl-sra,
.markdown-body .pl-sr .pl-sre {
    color: #032f62;
}

.markdown-body .pl-smw,
.markdown-body .pl-v {
    color: #e36209;
}

.markdown-body .pl-bu {
    color: #b31d28;
}

.markdown-body .pl-ii {
    background-color: #b31d28;
    color: #fafbfc;
}

.markdown-body .pl-c2 {
    background-color: #d73a49;
    color: #fafbfc;
}

.markdown-body .pl-c2:before {
    content: '^M';
}

.markdown-body .pl-sr .pl-cce {
    color: #22863a;
    font-weight: 700;
}

.markdown-body .pl-ml {
    color: #735c0f;
}

.markdown-body .pl-mh,
.markdown-body .pl-mh .pl-en,
.markdown-body .pl-ms {
    color: #005cc5;
    font-weight: 700;
}

.markdown-body .pl-mi {
    color: #24292e;
    font-style: italic;
}

.markdown-body .pl-mb {
    color: #24292e;
    font-weight: 700;
}

.markdown-body .pl-md {
    background-color: #ffeef0;
    color: #b31d28;
}

.markdown-body .pl-mi1 {
    background-color: #f0fff4;
    color: #22863a;
}

.markdown-body .pl-mc {
    background-color: #ffebda;
    color: #e36209;
}

.markdown-body .pl-mi2 {
    background-color: #005cc5;
    color: #f6f8fa;
}

.markdown-body .pl-mdr {
    color: #6f42c1;
    font-weight: 700;
}

.markdown-body .pl-ba {
    color: #586069;
}

.markdown-body .pl-sg {
    color: #959da5;
}

.markdown-body .pl-corl {
    color: #032f62;
    text-decoration: underline;
}

.markdown-body details {
    display: block;
}

.markdown-body summary {
    display: list-item;
}

.markdown-body a {
    background-color: transparent;
}

.markdown-body a:active,
.markdown-body a:hover {
    outline-width: 0;
}

.markdown-body strong {
    font-weight: inherit;
    font-weight: bolder;
}

.markdown-body h1 {
    font-size: 2em;
    margin: 0.67em 0;
}

.markdown-body img {
    border-style: none;
}

.markdown-body code,
.markdown-body kbd,
.markdown-body pre {
    font-family: monospace, monospace;
    font-size: 1em;
}

.markdown-body hr {
    box-sizing: content-box;
    height: 0;
    overflow: visible;
}

.markdown-body input {
    font: inherit;
    margin: 0;
}

.markdown-body input {
    overflow: visible;
}

.markdown-body [type='checkbox'] {
    box-sizing: border-box;
    padding: 0;
}

.markdown-body * {
    box-sizing: border-box;
}

.markdown-body input {
    font-family: inherit;
    font-size: inherit;
    line-height: inherit;
}

.markdown-body a {
    color: #0366d6;
    text-decoration: none;
}

.markdown-body a:hover {
    text-decoration: underline;
}

.markdown-body strong {
    font-weight: 600;
}

.markdown-body hr {
    background: transparent;
    border: 0;
    border-bottom: 1px solid #dfe2e5;
    height: 0;
    margin: 15px 0;
    overflow: hidden;
}

.markdown-body hr:before {
    content: '';
    display: table;
}

.markdown-body hr:after {
    clear: both;
    content: '';
    display: table;
}

.markdown-body table {
    border-collapse: collapse;
    border-spacing: 0;
}

.markdown-body td,
.markdown-body th {
    padding: 0;
}

.markdown-body details summary {
    cursor: pointer;
}

.markdown-body h1,
.markdown-body h2,
.markdown-body h3,
.markdown-body h4,
.markdown-body h5,
.markdown-body h6 {
    margin-bottom: 0;
    margin-top: 0;
}

.markdown-body h1 {
    font-size: 32px;
}

.markdown-body h1,
.markdown-body h2 {
    font-weight: 600;
}

.markdown-body h2 {
    font-size: 24px;
}

.markdown-body h3 {
    font-size: 20px;
}

.markdown-body h3,
.markdown-body h4 {
    font-weight: 600;
}

.markdown-body h4 {
    font-size: 16px;
}

.markdown-body h5 {
    font-size: 14px;
}

.markdown-body h5,
.markdown-body h6 {
    font-weight: 600;
}

.markdown-body h6 {
    font-size: 12px;
}

.markdown-body p {
    margin-bottom: 10px;
    margin-top: 0;
}

.markdown-body blockquote {
    margin: 0;
}

.markdown-body ol,
.markdown-body ul {
    margin-bottom: 0;
    margin-top: 0;
    padding-left: 0;
}

.markdown-body ol ol,
.markdown-body ul ol {
    list-style-type: lower-roman;
}

.markdown-body ol ol ol,
.markdown-body ol ul ol,
.markdown-body ul ol ol,
.markdown-body ul ul ol {
    list-style-type: lower-alpha;
}

.markdown-body dd {
    margin-left: 0;
}

.markdown-body code,
.markdown-body pre {
    font-family:
        SFMono-Regular,
        Consolas,
        Liberation Mono,
        Menlo,
        Courier,
        monospace;
    font-size: 12px;
}

.markdown-body pre {
    margin-bottom: 0;
    margin-top: 0;
}

.markdown-body input::-webkit-inner-spin-button,
.markdown-body input::-webkit-outer-spin-button {
    -webkit-appearance: none;
    appearance: none;
    margin: 0;
}

.markdown-body .border {
    border: 1px solid #e1e4e8 !important;
}

.markdown-body .border-0 {
    border: 0 !important;
}

.markdown-body .border-bottom {
    border-bottom: 1px solid #e1e4e8 !important;
}

.markdown-body .rounded-1 {
    border-radius: 3px !important;
}

.markdown-body .bg-white {
    background-color: #fff !important;
}

.markdown-body .bg-gray-light {
    background-color: #fafbfc !important;
}

.markdown-body .text-gray-light {
    color: #6a737d !important;
}

.markdown-body .mb-0 {
    margin-bottom: 0 !important;
}

.markdown-body .my-2 {
    margin-bottom: 8px !important;
    margin-top: 8px !important;
}

.markdown-body .pl-0 {
    padding-left: 0 !important;
}

.markdown-body .py-0 {
    padding-bottom: 0 !important;
    padding-top: 0 !important;
}

.markdown-body .pl-1 {
    padding-left: 4px !important;
}

.markdown-body .pl-2 {
    padding-left: 8px !important;
}

.markdown-body .py-2 {
    padding-bottom: 8px !important;
    padding-top: 8px !important;
}

.markdown-body .pl-3,
.markdown-body .px-3 {
    padding-left: 16px !important;
}

.markdown-body .px-3 {
    padding-right: 16px !important;
}

.markdown-body .pl-4 {
    padding-left: 24px !important;
}

.markdown-body .pl-5 {
    padding-left: 32px !important;
}

.markdown-body .pl-6 {
    padding-left: 40px !important;
}

.markdown-body .f6 {
    font-size: 12px !important;
}

.markdown-body .lh-condensed {
    line-height: 1.25 !important;
}

.markdown-body .text-bold {
    font-weight: 600 !important;
}

.markdown-body:before {
    content: '';
    display: table;
}

.markdown-body:after {
    clear: both;
    content: '';
    display: table;
}

.markdown-body > :first-child {
    margin-top: 0 !important;
}

.markdown-body > :last-child {
    margin-bottom: 0 !important;
}

.markdown-body a:not([href]) {
    color: inherit;
    text-decoration: none;
}

.markdown-body blockquote,
.markdown-body dl,
.markdown-body ol,
.markdown-body p,
.markdown-body pre,
.markdown-body table,
.markdown-body ul {
    margin-bottom: 16px;
    margin-top: 0;
}

.markdown-body hr {
    background-color: #e1e4e8;
    border: 0;
    height: 0.25em;
    margin: 24px 0;
    padding: 0;
}

.markdown-body blockquote {
    border-left: 0.25em solid #dfe2e5;
    color: #6a737d;
    padding: 0 1em;
}

.markdown-body blockquote > :first-child {
    margin-top: 0;
}

.markdown-body blockquote > :last-child {
    margin-bottom: 0;
}

.markdown-body kbd {
    background-color: #fafbfc;
    border: 1px solid #c6cbd1;
    border-bottom-color: #959da5;
    border-radius: 3px;
    box-shadow: inset 0 -1px 0 #959da5;
    color: #444d56;
    display: inline-block;
    font-size: 11px;
    line-height: 10px;
    padding: 3px 5px;
    vertical-align: middle;
}

.markdown-body h1,
.markdown-body h2,
.markdown-body h3,
.markdown-body h4,
.markdown-body h5,
.markdown-body h6 {
    font-weight: 600;
    line-height: 1.25;
    margin-bottom: 16px;
    margin-top: 24px;
}

.markdown-body h1 {
    font-size: 2em;
}

.markdown-body h1,
.markdown-body h2 {
    border-bottom: 1px solid #eaecef;
    padding-bottom: 0.3em;
}

.markdown-body h2 {
    font-size: 1.5em;
}

.markdown-body h3 {
    font-size: 1.25em;
}

.markdown-body h4 {
    font-size: 1em;
}

.markdown-body h5 {
    font-size: 0.875em;
}

.markdown-body h6 {
    color: #6a737d;
    font-size: 0.85em;
}

.markdown-body ol,
.markdown-body ul {
    padding-left: 2em;
}

.markdown-body ol ol,
.markdown-body ol ul,
.markdown-body ul ol,
.markdown-body ul ul {
    margin-bottom: 0;
    margin-top: 0;
}

.markdown-body li {
    word-wrap: break-all;
}

.markdown-body li > p {
    margin-top: 16px;
}

.markdown-body li + li {
    margin-top: 0.25em;
}

.markdown-body dl {
    padding: 0;
}

.markdown-body dl dt {
    font-size: 1em;
    font-style: italic;
    font-weight: 600;
    margin-top: 16px;
    padding: 0;
}

.markdown-body dl dd {
    margin-bottom: 16px;
    padding: 0 16px;
}

.markdown-body table {
    display: block;
    overflow: auto;
    width: 100%;
}

.markdown-body table th {
    font-weight: 600;
}

.markdown-body table td,
.markdown-body table th {
    border: 1px solid #dfe2e5;
    padding: 6px 13px;
}

.markdown-body table tr {
    background-color: #fff;
    border-top: 1px solid #c6cbd1;
}

.markdown-body table tr:nth-child(2n) {
    background-color: #f6f8fa;
}

.markdown-body img {
    background-color: #fff;
    box-sizing: content-box;
    max-width: 100%;
}

.markdown-body img[align='right'] {
    padding-left: 20px;
}

.markdown-body img[align='left'] {
    padding-right: 20px;
}

.markdown-body code {
    background-color: rgba(27, 31, 35, 0.05);
    border-radius: 3px;
    font-size: 85%;
    margin: 0;
    padding: 0.2em 0.4em;
}

.markdown-body pre {
    word-wrap: normal;
}

.markdown-body pre > code {
    background: transparent;
    border: 0;
    font-size: 100%;
    margin: 0;
    padding: 0;
    white-space: pre;
    word-break: normal;
}

.markdown-body .highlight {
    margin-bottom: 16px;
}

.markdown-body .highlight pre {
    margin-bottom: 0;
    word-break: normal;
}

.markdown-body .highlight pre,
.markdown-body pre {
    background-color: #f6f8fa;
    border-radius: 3px;
    font-size: 85%;
    line-height: 1.45;
    overflow: auto;
    padding: 16px;
}

.markdown-body pre code {
    background-color: transparent;
    border: 0;
    display: inline;
    line-height: inherit;
    margin: 0;
    max-width: auto;
    overflow: visible;
    padding: 0;
    word-wrap: normal;
}

.markdown-body .commit-tease-sha {
    color: #444d56;
    display: inline-block;
    font-family:
        SFMono-Regular,
        Consolas,
        Liberation Mono,
        Menlo,
        Courier,
        monospace;
    font-size: 90%;
}

.markdown-body .blob-wrapper {
    border-bottom-left-radius: 3px;
    border-bottom-right-radius: 3px;
    overflow-x: auto;
    overflow-y: hidden;
}

.markdown-body .blob-wrapper-embedded {
    max-height: 240px;
    overflow-y: auto;
}

.markdown-body .blob-num {
    -moz-user-select: none;
    -ms-user-select: none;
    -webkit-user-select: none;
    color: rgba(27, 31, 35, 0.3);
    cursor: pointer;
    font-family:
        SFMono-Regular,
        Consolas,
        Liberation Mono,
        Menlo,
        Courier,
        monospace;
    font-size: 12px;
    line-height: 20px;
    min-width: 50px;
    padding-left: 10px;
    padding-right: 10px;
    text-align: right;
    user-select: none;
    vertical-align: top;
    white-space: nowrap;
    width: 1%;
}

.markdown-body .blob-num:hover {
    color: rgba(27, 31, 35, 0.6);
}

.markdown-body .blob-num:before {
    content: attr(data-line-number);
}

.markdown-body .blob-code {
    line-height: 20px;
    padding-left: 10px;
    padding-right: 10px;
    position: relative;
    vertical-align: top;
}

.markdown-body .blob-code-inner {
    color: #24292e;
    font-family:
        SFMono-Regular,
        Consolas,
        Liberation Mono,
        Menlo,
        Courier,
        monospace;
    font-size: 12px;
    overflow: visible;
    white-space: pre;
    word-wrap: normal;
}

.markdown-body .pl-token.active,
.markdown-body .pl-token:hover {
    background: #ffea7f;
    cursor: pointer;
}

.markdown-body kbd {
    background-color: #fafbfc;
    border: 1px solid #d1d5da;
    border-bottom-color: #c6cbd1;
    border-radius: 3px;
    box-shadow: inset 0 -1px 0 #c6cbd1;
    color: #444d56;
    display: inline-block;
    font:
        11px SFMono-Regular,
        Consolas,
        Liberation Mono,
        Menlo,
        Courier,
        monospace;
    line-height: 10px;
    padding: 3px 5px;
    vertical-align: middle;
}

.markdown-body :checked + .radio-label {
    border-color: #0366d6;
    position: relative;
    z-index: 1;
}

.markdown-body .tab-size[data-tab-size='1'] {
    -moz-tab-size: 1;
    tab-size: 1;
}

.markdown-body .tab-size[data-tab-size='2'] {
    -moz-tab-size: 2;
    tab-size: 2;
}

.markdown-body .tab-size[data-tab-size='3'] {
    -moz-tab-size: 3;
    tab-size: 3;
}

.markdown-body .tab-size[data-tab-size='4'] {
    -moz-tab-size: 4;
    tab-size: 4;
}

.markdown-body .tab-size[data-tab-size='5'] {
    -moz-tab-size: 5;
    tab-size: 5;
}

.markdown-body .tab-size[data-tab-size='6'] {
    -moz-tab-size: 6;
    tab-size: 6;
}

.markdown-body .tab-size[data-tab-size='7'] {
    -moz-tab-size: 7;
    tab-size: 7;
}

.markdown-body .tab-size[data-tab-size='8'] {
    -moz-tab-size: 8;
    tab-size: 8;
}

.markdown-body .tab-size[data-tab-size='9'] {
    -moz-tab-size: 9;
    tab-size: 9;
}

.markdown-body .tab-size[data-tab-size='10'] {
    -moz-tab-size: 10;
    tab-size: 10;
}

.markdown-body .tab-size[data-tab-size='11'] {
    -moz-tab-size: 11;
    tab-size: 11;
}

.markdown-body .tab-size[data-tab-size='12'] {
    -moz-tab-size: 12;
    tab-size: 12;
}

.markdown-body .task-list-item {
    list-style-type: none;
}

.markdown-body .task-list-item + .task-list-item {
    margin-top: 3px;
}

.markdown-body .task-list-item input {
    margin: 0 0.2em 0.25em -1.6em;
    vertical-align: middle;
}

.markdown-body hr {
    border-bottom-color: #eee;
}

.markdown-body .pl-0 {
    padding-left: 0 !important;
}

.markdown-body .pl-1 {
    padding-left: 4px !important;
}

.markdown-body .pl-2 {
    padding-left: 8px !important;
}

.markdown-body .pl-3 {
    padding-left: 16px !important;
}

.markdown-body .pl-4 {
    padding-left: 24px !important;
}

.markdown-body .pl-5 {
    padding-left: 32px !important;
}

.markdown-body .pl-6 {
    padding-left: 40px !important;
}

.markdown-body .pl-7 {
    padding-left: 48px !important;
}

.markdown-body .pl-8 {
    padding-left: 64px !important;
}

.markdown-body .pl-9 {
    padding-left: 80px !important;
}

.markdown-body .pl-10 {
    padding-left: 96px !important;
}

.markdown-body .pl-11 {
    padding-left: 112px !important;
}

.markdown-body .pl-12 {
    padding-left: 128px !important;
}

code[class*='language-'],
pre[class*='language-'] {
    color: black;
    background: none;
    text-shadow: 0 1px white;
    font-family: Consolas, Monaco, 'Andale Mono', 'Ubuntu Mono', monospace;
    font-size: 1em;
    text-align: left;
    white-space: pre;
    word-spacing: normal;
    word-break: normal;
    word-wrap: normal;
    line-height: 1.5;

    -moz-tab-size: 4;
    -o-tab-size: 4;
    tab-size: 4;

    -webkit-hyphens: none;
    -moz-hyphens: none;
    -ms-hyphens: none;
    hyphens: none;
}

pre[class*='language-']::-moz-selection,
pre[class*='language-'] ::-moz-selection,
code[class*='language-']::-moz-selection,
code[class*='language-'] ::-moz-selection {
    text-shadow: none;
    background: #b3d4fc;
}

pre[class*='language-']::selection,
pre[class*='language-'] ::selection,
code[class*='language-']::selection,
code[class*='language-'] ::selection {
    text-shadow: none;
    background: #b3d4fc;
}

@media print {
    code[class*='language-'],
    pre[class*='language-'] {
        text-shadow: none;
    }
}

/* Code blocks */
pre[class*='language-'] {
    padding: 1em;
    margin: 0.5em 0;
    overflow: auto;
}

:not(pre) > code[class*='language-'],
pre[class*='language-'] {
    background: #f5f2f0;
}

/* Inline code */
:not(pre) > code[class*='language-'] {
    padding: 0.1em;
    border-radius: 0.3em;
    white-space: normal;
}

.token.comment,
.token.prolog,
.token.doctype,
.token.cdata {
    color: slategray;
}

.token.punctuation {
    color: #999;
}

.token.namespace {
    opacity: 0.7;
}

.token.property,
.token.tag,
.token.boolean,
.token.number,
.token.constant,
.token.symbol,
.token.deleted {
    color: #905;
}

.token.selector,
.token.attr-name,
.token.string,
.token.char,
.token.builtin,
.token.inserted {
    color: #690;
}

.token.operator,
.token.entity,
.token.url,
.language-css .token.string,
.style .token.string {
    color: #9a6e3a;
    /* This background color was intended by the author of this theme. */
    background: hsla(0, 0%, 100%, 0.5);
}

.token.atrule,
.token.attr-value,
.token.keyword {
    color: #07a;
}

.token.function,
.token.class-name {
    color: #dd4a68;
}

.token.regex,
.token.important,
.token.variable {
    color: #e90;
}

.token.important,
.token.bold {
    font-weight: bold;
}

.token.italic {
    font-style: italic;
}

.token.entity {
    cursor: help;
}

.markdown-body {
    font-family:
        -apple-system,
        Segoe UI,
        Helvetica,
        Arial,
        sans-serif,
        Apple Color Emoji,
        Segoe UI Emoji;
    box-sizing: border-box;
    min-width: 200px;
    max-width: 980px;
}

@media not print {
    .markdown-body {
        padding: 15px;
    }

    @media (max-width: 767px) {
        .markdown-body {
            padding: 15px;
        }
    }
}

.hf-container {
    color: #24292e;
    line-height: 1.3;
}

.markdown-body .highlight pre,
.markdown-body pre {
    white-space: pre-wrap;
}

.markdown-body table {
    display: table;
}

.markdown-body img[data-align='center'] {
    display: block;
    margin: 0 auto;
}

.markdown-body img[data-align='right'] {
    display: block;
    margin: 0 0 0 auto;
}

.markdown-body li.task-list-item {
    list-style-type: none;
}

.markdown-body li > [type='checkbox'] {
    margin: 0 0 0 -1.3em;
}

.markdown-body input[type='checkbox'] ~ p {
    margin-top: 0;
    display: inline-block;
}

.markdown-body ol ol,
.markdown-body ul ol {
    list-style-type: decimal;
}

.markdown-body ol ol ol,
.markdown-body ol ul ol,
.markdown-body ul ol ol,
.markdown-body ul ul ol {
    list-style-type: decimal;
}

.markdown-body a.footnote-ref {
    text-decoration: none;
}

.footnotes {
    font-size: 0.85em;
    opacity: 0.8;
}

.footnotes li[role='doc-endnote'] {
    position: relative;
}

.footnotes .footnote-back {
    position: absolute;
    font-family: initial;
    top: 0.2em;
    right: 1em;
    text-decoration: none;
}

.inline-math.invalid,
.multiple-math.invalid {
    color: rgb(255, 105, 105);
}

.toc-container {
    width: 100%;
}

.toc-container .toc-title {
    font-weight: 700;
    font-size: 1.2em;
    margin-bottom: 0;
}

.toc-container li,
.toc-container ul,
.toc-container ul li {
    list-style: none !important;
}

.toc-container > ul {
    padding-left: 0;
}

.toc-container ul li span {
    display: flex;
}

.toc-container ul li span a {
    color: inherit;
    text-decoration: none;
}

.toc-container ul li span a:hover {
    color: inherit;
    text-decoration: none;
}

.toc-container ul li span span.dots {
    flex: 1;
    height: 0.65em;
    margin: 0 10px;
    border-bottom: 2px dotted black;
}

pre.front-matter {
    display: none !important;
}
</style>
